import React, { useState } from 'react';
import styled from 'styled-components';
import { FieldPanelFullWidth, HeaderCaptionSemiBold, StyledInput } from 'styles/v2/Styles.styled';
import ChangePasswordDialog from './ChangePasswordDialog';
import { Button } from 'primereact/button';
import ChangeUserNameDialog from './ChangeUserNameDialog';
import useCurrentUser from 'hooks/useCurrentUser';
import { RoleName } from 'constants/constants';
import { HelpBox } from 'components/v2/help-boxes';
import { HyperLink } from 'styles/Styles.styled';
import ChangeEmailDialog from './ChangeEmailDialog';
import UserRolesTooltip from './UserRolesTooltip';

type VisibleDialogType = 'changeUserName' | 'changePassword' | 'changeEmail';

const UserSettingsMyAccountTab: React.FC<object> = () => {
  const [visibleDialog, setVisibleDialog] = useState<VisibleDialogType | null>(null);
  const hideDialog = () => setVisibleDialog(null);
  const { roles, memberships, firstName, lastName, email } = useCurrentUser();

  const systemRoles = roles.map((r) => r.name).join(', ');
  const userRoles = memberships
    .map((m) => m.roleName)
    .filter((x, i, arr) => arr.indexOf(x) === i)
    .join(', ');

  const haveVerifierRole = memberships?.some((m) => m.roleName === RoleName.EPD_VERIFIER) ?? false;

  return (
    <MyAccountTabContainer>
      <ChangeUserNameDialog visible={visibleDialog === 'changeUserName'} onHide={hideDialog} />
      <ChangePasswordDialog isOpened={visibleDialog === 'changePassword'} onHide={hideDialog} />
      <ChangeEmailDialog visible={visibleDialog === 'changeEmail'} onHide={hideDialog} />
      {(systemRoles.length > 0 || userRoles.length > 0) && (
        <RoleBadge>
          {systemRoles.length > 0 && (
            <div>
              System role: <ListOfRoles>{systemRoles}</ListOfRoles>
            </div>
          )}
          {userRoles.length > 0 && (
            <div>
              User role: <ListOfRoles>{userRoles}</ListOfRoles>
              {memberships?.length > 0 && <UserRolesTooltip memberships={memberships} />}
            </div>
          )}
        </RoleBadge>
      )}
      <FieldPanelFullWidth>
        <HeaderRow>
          <HeaderCaptionSemiBold className="white-space-nowrap">User name</HeaderCaptionSemiBold>
          <ActionButton onClick={() => setVisibleDialog('changeUserName')}>Change user name</ActionButton>
        </HeaderRow>
        <FieldRow>
          <Field>
            <label>First name</label>
            <StyledInput value={firstName || ''} disabled />
          </Field>
          <Field>
            <label>Last name</label>
            <StyledInput value={lastName || ''} disabled />
          </Field>
        </FieldRow>
      </FieldPanelFullWidth>
      <FieldPanelFullWidth>
        <HeaderRow>
          <HeaderCaptionSemiBold className="white-space-nowrap">Email Address</HeaderCaptionSemiBold>
          {!haveVerifierRole && (
            <ActionButton onClick={() => setVisibleDialog('changeEmail')}>Change email address</ActionButton>
          )}
        </HeaderRow>

        {haveVerifierRole && (
          <HelpBox>
            <span>
              To change the email address, please contact{' '}
              <HyperLink href="mailto:support@environdec.com">support@environdec.com</HyperLink>
            </span>
          </HelpBox>
        )}

        <FieldRow>
          <Field>
            <label>Current email</label>
            <StyledInput value={email} disabled />
          </Field>
        </FieldRow>
      </FieldPanelFullWidth>
      <FieldPanelFullWidth>
        <HeaderRow>
          <HeaderCaptionSemiBold className="white-space-nowrap">PASSWORD</HeaderCaptionSemiBold>
          <ActionButton onClick={() => setVisibleDialog('changePassword')}>Change password</ActionButton>
        </HeaderRow>
      </FieldPanelFullWidth>
    </MyAccountTabContainer>
  );
};

const MyAccountTabContainer = styled.div`
  && > div:first-child {
    margin-top: 0;
  }
`;

const RoleBadge = styled.div`
  background-color: ${(props) => props.theme.colors.secondaryLightGreen30};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-content: center;
  padding: 15px 30px;
  border-radius: 4px;
  font-size: 0.875rem;
  gap: 0.5rem;
  color: ${(props) => props.theme.colors.primaryGreen};
`;

const ListOfRoles = styled.span`
  font-weight: 600;
  margin-left: 2px;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Field = styled.div`
  flex: 1;
  display: grid;

  label {
    font-size: 0.75rem;
    text-transform: uppercase;
    color: ${(props) => props.theme.colors.primaryBlack};
    padding-bottom: 4px;
  }
`;

const ActionButton = styled(Button).attrs({ text: true })`
  color: ${(props) => props.theme.colors.primaryOrange};
  font-size: 0.875rem;
  font-family: inherit;
  line-height: 1.3rem;
  font-weight: 500;
  padding: 0;
  border: none;
  border-radius: 2px;
  background-color: transparent;
  &&.p-button:hover {
    background-color: transparent;
  }
  &&:focus-visible {
    outline: 2px solid #000000;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export default UserSettingsMyAccountTab;
